import { useEffect, useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MdEdit, MdDelete, MdAdd, MdSecurity, MdCheckCircle, MdSearch, MdKeyboardArrowRight, MdCategory, MdInfoOutline, MdHistory, MdArrowBack } from "react-icons/md";
import { useAuth } from "../../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { authenticatedFetch } from "../../lib/api";
import { ADMIN_VESSEL_PERMISSIONS } from "../../utils/vesselPermissions";
import { ClearFiltersButton, PageHeader, erpPageBtnPrimaryClass, erpPageBtnSecondaryClass } from "@euroasiann/shared/ui";

const ADMIN_VESSEL_PERMISSION_KEYS = Object.values(ADMIN_VESSEL_PERMISSIONS);

function slugRoleKey(name: string, portalType = "admin") {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!base) return "";
  return base.startsWith(`${portalType}_`) ? base : `${portalType}_${base}`;
}

function isFullAdminRole(role: { name?: string; key?: string }) {
  const name = (role.name || "").toLowerCase().trim();
  const key = (role.key || "").toLowerCase();
  return (
    key === "admin_super_admin" ||
    key === "admin_platform_admin" ||
    key === "admin_superuser" ||
    key.includes("super_admin") ||
    name === "admin" ||
    name === "super admin" ||
    name === "platform admin"
  );
}

export function RolesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [roleName, setRoleName] = useState("");
  const [roleKey, setRoleKey] = useState("");
  const [roleKeyDirty, setRoleKeyDirty] = useState(false);
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");

  const submitRoleSearch = () => {
    setSubmittedSearch(searchQuery.trim());
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSubmittedSearch("");
  };

  // Initialize URL params from user data
  useEffect(() => {
    if (user?.organizationId) {
      const params = new URLSearchParams(searchParams);
      if (!params.get("portalType")) {
        params.set("portalType", "admin");
      }
      if (!params.get("organizationId")) {
        params.set("organizationId", user.organizationId);
      }
      setSearchParams(params, { replace: true });
    }
  }, [user?.organizationId]);

  const permissionsQuery = useQuery({
    queryKey: ["admin-permission-catalog"],
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/v1/permissions?portalType=admin`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to fetch permissions");
      return json.data as any[];
    },
  });

  const rolesQuery = useQuery({
    queryKey: ["admin-org-roles", user?.organizationId, submittedSearch],
    enabled: Boolean(user?.organizationId),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("portalType", "admin");
      params.set("organizationId", user!.organizationId!);
      if (submittedSearch) {
        params.set("name", submittedSearch);
      }
      const res = await authenticatedFetch(`/api/v1/roles?${params.toString()}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to fetch roles");
      return Array.isArray(json.data) ? json.data : [];
    },
  });

  const permissionsList = permissionsQuery.data || [];
  const roles = useMemo(() => {
    return (rolesQuery.data || []).map((r: any) => ({
      _id: r._id,
      key: r.key,
      portal: r.portalType,
      name: r.name,
      permissionsMap: permissionsList.reduce((acc: any, p: any) => {
        acc[p.key] = r.permissions?.includes(p.key) ?? false;
        if (isFullAdminRole(r) && ADMIN_VESSEL_PERMISSION_KEYS.includes(p.key)) {
          acc[p.key] = true;
        }
        return acc;
      }, {}),
    }));
  }, [rolesQuery.data, permissionsList]);

  useEffect(() => {
    if (!permissionsList.length) return;
    setPermissions((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const map: Record<string, boolean> = {};
      permissionsList.forEach((p: any) => (map[p.key] = false));
      return map;
    });
  }, [permissionsList]);

  useEffect(() => {
    if (roles.length === 0 || isEditMode) return;
    setSelectedRole((prev: any) => {
      if (!prev) return roles[0];
      return roles.find((r: any) => r._id === prev._id) ?? prev;
    });
  }, [roles, isEditMode]);

  const refreshRoles = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin-org-roles"] });
  };

  useEffect(() => {
    if (selectedRole || !isEditMode) return;
    if (!isFullAdminRole({ name: roleName })) return;
    setPermissions((prev) => {
      const next = { ...prev };
      ADMIN_VESSEL_PERMISSION_KEYS.forEach((key) => {
        next[key] = true;
      });
      return next;
    });
  }, [roleName, selectedRole, isEditMode]);

  // ⭐ Group permissions into categories
  const categorizedPermissions = useMemo(() => {
    const categories: Record<string, any[]> = {
      Overview: [],
      Vessels: [],
      RFQs: [],
      Users: [],
      System: [],
      Inventory: [],
      Other: []
    };

    permissionsList.forEach(p => {
      const key = p.key.toLowerCase();
      if (key.includes('dashboard') || key.includes('analytics')) categories.Overview.push(p);
      else if (key.includes('vessel')) categories.Vessels.push(p);
      else if (key.includes('rfq') || key.includes('order') || key.includes('po')) categories.RFQs.push(p);
      else if (key.includes('user') || key.includes('role')) categories.Users.push(p);
      else if (key.includes('system') || key.includes('config') || key.includes('setting')) categories.System.push(p);
      else if (key.includes('inventory') || key.includes('catalog')) categories.Inventory.push(p);
      else categories.Other.push(p);
    });

    return Object.fromEntries(Object.entries(categories).filter(([_, list]) => list.length > 0));
  }, [permissionsList]);

  // ⭐ Create Role
  const handleAddRole = async () => {
    if (!roleName.trim()) return;
    const key = (roleKey.trim() || slugRoleKey(roleName)).replace(/^_+|_+$/g, "");
    if (!key) return;

    setIsCreating(true);
    const selectedPermissions = Object.keys(permissions).filter((p) => permissions[p]);
    if (isFullAdminRole({ name: roleName })) {
      ADMIN_VESSEL_PERMISSION_KEYS.forEach((key) => {
        if (!selectedPermissions.includes(key)) selectedPermissions.push(key);
      });
    }

    try {
      const res = await authenticatedFetch(`/api/v1/roles`, {
        method: "POST",
        body: JSON.stringify({
          name: roleName.trim(),
          key,
          portalType: "admin",
          permissions: selectedPermissions,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        alert(error.error || "Failed to create role");
        setIsCreating(false);
        return;
      }

      const json = await res.json();
      if (json.success) {
        setRoleName("");
        setRoleKey("");
        setRoleKeyDirty(false);
        setPermissions(Object.fromEntries(permissionsList.map(p => [p.key, false])));
        setIsEditMode(false);
        refreshRoles();
      }
    } catch (err: any) {
      console.error("Error creating role:", err);
      alert(err.message || "Failed to create role");
    } finally {
      setIsCreating(false);
    }
  };

  // ⭐ Save Edit
  const saveEdit = async () => {
    if (!selectedRole) return;
    setLoading(true);

    const selectedPermissions = [
      ...new Set([
        ...permissionsList.filter((p) => selectedRole.permissionsMap[p.key]).map((p) => p.key),
        ...(isFullAdminRole(selectedRole) ? ADMIN_VESSEL_PERMISSION_KEYS : []),
      ]),
    ];

    try {
      const res = await authenticatedFetch(`/api/v1/roles/${selectedRole._id}`, {
        method: "PUT",
        body: JSON.stringify({ permissions: selectedPermissions }),
      });

      if (!res.ok) {
        const error = await res.json();
        alert(error.error || "Failed to update role");
        return;
      }

      setIsEditMode(false);
      refreshRoles();
    } catch (err: any) {
      console.error("Error updating role:", err);
      alert(err.message || "Failed to update role");
    } finally {
      setLoading(false);
    }
  };

  // ⭐ Delete Role
  const handleDeleteRole = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this role?")) return;

    try {
      const res = await authenticatedFetch(`/api/v1/roles/${id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const error = await res.json();
        alert(error.error || "Failed to delete role");
        return;
      }

      if (selectedRole?._id === id) setSelectedRole(null);
      refreshRoles();
    } catch (err: any) {
      console.error("Error deleting role:", err);
      alert(err.message || "Failed to delete role");
    }
  };

  const isCreatingRole = isEditMode && !selectedRole;
  const showDetailPanel = Boolean(selectedRole || isEditMode);
  const permissionCount = selectedRole
    ? Object.values(selectedRole.permissionsMap).filter(Boolean).length
    : Object.values(permissions).filter(Boolean).length;

  const exitCreateOrEdit = () => {
    setIsEditMode(false);
    setRoleName("");
    setRoleKey("");
    setRoleKeyDirty(false);
    if (!selectedRole) {
      setSelectedRole(roles[0] ?? null);
    } else {
      refreshRoles();
    }
  };

  const togglePermission = (permKey: string) => {
    const keepVesselSelected =
      selectedRole &&
      isFullAdminRole(selectedRole) &&
      ADMIN_VESSEL_PERMISSION_KEYS.includes(permKey);
    if (keepVesselSelected) return;
    if (selectedRole) {
      setSelectedRole({
        ...selectedRole,
        permissionsMap: {
          ...selectedRole.permissionsMap,
          [permKey]: !selectedRole.permissionsMap[permKey],
        },
      });
      return;
    }
    setPermissions({
      ...permissions,
      [permKey]: !permissions[permKey],
    });
  };

  const renderPermissionCategories = (editable: boolean) => (
    <div className="space-y-5">
      {Object.entries(categorizedPermissions).map(([category, perms]) => (
        <div key={category}>
          <div className="mb-2 flex h-6 items-center gap-2">
            <MdCategory className="h-4 w-4 shrink-0 text-[#0088cc]" />
            <h3 className="shrink-0 text-sm font-semibold text-[hsl(var(--foreground))]">{category}</h3>
            <div className="h-px min-w-0 flex-1 bg-[hsl(var(--border))]" />
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {perms.map((perm) => {
              const checked = !!(selectedRole
                ? selectedRole.permissionsMap[perm.key]
                : permissions[perm.key]);
              return (
                <label
                  key={perm.key}
                  className={`flex min-h-[3.25rem] cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                    !editable
                      ? checked
                        ? "cursor-default border-[#0088cc]/25 bg-[#0088cc]/5"
                        : "cursor-default border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 opacity-70"
                      : checked
                        ? "border-[#0088cc]/40 bg-[#0088cc]/10"
                        : "border-[hsl(var(--border))] bg-[hsl(var(--background))] hover:border-[#0088cc]/50"
                  }`}
                >
                  <input
                    type="checkbox"
                    disabled={!editable}
                    checked={checked}
                    onChange={() => togglePermission(perm.key)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-[hsl(var(--border))] text-[#0088cc] focus:ring-[#0088cc]"
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-[hsl(var(--foreground))]">
                      {perm.label}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-[hsl(var(--muted-foreground))]">
                      {perm.key}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
      <PageHeader
        title={isCreatingRole ? "Create role" : "Roles & permissions"}
        subtitle={
          isCreatingRole
            ? "Name the role, then choose the permissions it should have."
            : "Manage who can access features in your organization"
        }
        subtitleVariant="description"
        subtitleAccent={false}
        breadcrumb={
          isCreatingRole
            ? [
                { label: "Admin" },
                { label: "Roles & permissions" },
                { label: "Create role", current: true },
              ]
            : [
                { label: "Admin" },
                { label: "Roles & permissions", current: true },
              ]
        }
        actions={
          isCreatingRole ? (
            <button type="button" onClick={exitCreateOrEdit} className={erpPageBtnSecondaryClass}>
              <MdArrowBack className="h-5 w-5" />
              Back to roles
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setSelectedRole(null);
                setIsEditMode(true);
                setRoleName("");
                setRoleKey("");
                setRoleKeyDirty(false);
                setPermissions(Object.fromEntries(permissionsList.map((p) => [p.key, false])));
              }}
              className={erpPageBtnPrimaryClass}
            >
              <MdAdd className="h-5 w-5" />
              Create role
            </button>
          )
        }
      />

      <div className="mt-6 grid h-0 min-h-0 max-h-full flex-1 grid-cols-1 overflow-hidden rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] md:grid-cols-[17.5rem_minmax(0,1fr)] lg:grid-cols-[19.5rem_minmax(0,1fr)] xl:grid-cols-[21.5rem_minmax(0,1fr)]">
        <div
          className={`h-full min-h-0 max-h-full flex-col overflow-hidden md:border-r md:border-[hsl(var(--border))] ${
            showDetailPanel ? "hidden md:flex" : "flex"
          }`}
        >
          <div className="flex min-h-[3.5rem] shrink-0 items-center gap-2 border-b border-[hsl(var(--border))] px-4">
            <div className="relative min-w-0 flex-1">
              <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
              <input
                type="search"
                placeholder="Search roles"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitRoleSearch();
                  }
                }}
                className="h-11 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] py-0 pl-9 pr-3 text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[#0088cc] focus:outline-none focus:ring-1 focus:ring-[#0088cc]"
              />
            </div>
            <ClearFiltersButton
              visible={!!searchQuery.trim()}
              onClick={clearSearch}
              variant="toolbar"
            />
          </div>

          <div className="h-0 min-h-0 flex-1 overflow-y-auto px-2 py-2 pb-6">
            {isCreatingRole && (
              <div className="mb-1 flex h-12 w-full items-center gap-3 rounded-lg border border-dashed border-[#0088cc]/40 bg-[#0088cc]/10 px-3 text-left text-[#0088cc]">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0088cc] text-white">
                  <MdAdd className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold leading-tight">
                    {roleName.trim() || "New role"}
                  </p>
                  <p className="truncate text-xs leading-tight text-[#0088cc]/80">Draft · not saved</p>
                </div>
              </div>
            )}
            {rolesQuery.isFetching && roles.length === 0 ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="mx-1 mb-1 h-12 animate-pulse rounded-lg bg-[hsl(var(--muted))]" />
              ))
            ) : roles.length === 0 ? (
              <div className="flex flex-col items-center px-4 py-12 text-center text-[hsl(var(--muted-foreground))]">
                <MdInfoOutline className="mb-2 h-8 w-8 opacity-40" />
                <p className="text-sm font-medium">
                  {submittedSearch ? "No matching roles" : "No roles found"}
                </p>
              </div>
            ) : (
              roles.map((role) => {
                const active = selectedRole?._id === role._id && !isEditMode;
                return (
                  <button
                    key={role._id}
                    type="button"
                    onClick={() => {
                      setSelectedRole(role);
                      setIsEditMode(false);
                    }}
                    className={`flex min-h-12 w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                      active
                        ? "bg-[#0088cc]/10 text-[#0088cc]"
                        : "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
                    }`}
                  >
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                        active ? "bg-[#0088cc] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
                      }`}
                    >
                      <MdSecurity className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold leading-tight">{role.name}</p>
                      <p className="truncate text-xs leading-tight text-[hsl(var(--muted-foreground))]">
                        {Object.values(role.permissionsMap).filter(Boolean).length} permissions
                      </p>
                    </div>
                    <MdKeyboardArrowRight className="h-4 w-4 shrink-0 opacity-40" />
                  </button>
                );
              })
            )}
          </div>
        </div>

        <section
          className={`h-full min-h-0 max-h-full flex-col overflow-hidden ${
            showDetailPanel ? "flex" : "hidden md:flex"
          }`}
        >
          {!showDetailPanel ? (
            <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center px-8 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#0088cc]/10">
                <MdSecurity className="h-6 w-6 text-[#0088cc]" />
              </div>
              <h2 className="text-base font-semibold text-[hsl(var(--foreground))]">Select a role</h2>
              <p className="mt-1 max-w-sm text-sm text-[hsl(var(--muted-foreground))]">
                Choose a role from the list to view or edit its permissions.
              </p>
            </div>
          ) : isCreatingRole ? (
            <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[hsl(var(--background))]">
              <div className="shrink-0 border-b border-[#0088cc]/20 bg-[#0088cc]/10 px-4 py-4 lg:px-6">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0088cc] text-white">
                    <MdAdd className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-semibold text-[hsl(var(--foreground))]">New role</h2>
                      <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                        Draft
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-[hsl(var(--muted-foreground))]">
                      This role is not saved until you create it.
                    </p>
                  </div>
                </div>
              </div>

              <div className="h-0 min-h-0 flex-1 overflow-y-auto px-4 py-5 pb-8 lg:px-6">
                <section className="mb-5 overflow-hidden rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                  <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 px-4 py-2.5">
                    <h3 className="text-sm font-semibold text-[hsl(var(--foreground))]">Name</h3>
                  </div>
                  <div className="p-4">
                    <label htmlFor="new-role-name" className="sr-only">Name</label>
                    <input
                      id="new-role-name"
                      autoFocus
                      value={roleName}
                      onChange={(e) => {
                        const next = e.target.value;
                        setRoleName(next);
                        if (!roleKeyDirty) setRoleKey(slugRoleKey(next));
                      }}
                      placeholder="e.g. Fleet Manager"
                      className="h-11 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[#0088cc] focus:outline-none focus:ring-1 focus:ring-[#0088cc]"
                    />
                    <p className="mt-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                      Display name people will see when assigning this access.
                    </p>
                  </div>
                </section>

                <section className="mb-6 overflow-hidden rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                  <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 px-4 py-2.5">
                    <h3 className="text-sm font-semibold text-[hsl(var(--foreground))]">Role</h3>
                  </div>
                  <div className="p-4">
                    <label htmlFor="new-role-key" className="sr-only">Role</label>
                    <input
                      id="new-role-key"
                      value={roleKey}
                      onChange={(e) => {
                        setRoleKeyDirty(true);
                        setRoleKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]+/g, "_"));
                      }}
                      placeholder="e.g. admin_fleet_manager"
                      className="h-11 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 font-mono text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[#0088cc] focus:outline-none focus:ring-1 focus:ring-[#0088cc]"
                    />
                    <p className="mt-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                      Internal role key used for permissions. Auto-filled from the name.
                    </p>
                  </div>
                </section>

                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-[hsl(var(--foreground))]">Assign permissions</h3>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                      Select every feature this role should be able to use.
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-[#0088cc]/10 px-2.5 py-1 text-xs font-semibold text-[#0088cc]">
                    {permissionCount} selected
                  </span>
                </div>
                {renderPermissionCategories(true)}
              </div>

              <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 py-3 lg:px-6">
                <button type="button" onClick={exitCreateOrEdit} className={erpPageBtnSecondaryClass}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddRole}
                  disabled={isCreating || !roleName.trim() || !(roleKey.trim() || slugRoleKey(roleName))}
                  className={erpPageBtnPrimaryClass}
                >
                  {isCreating ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <MdAdd className="h-4 w-4" />
                  )}
                  Create role
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex min-h-[3.5rem] shrink-0 items-center gap-3 border-b border-[hsl(var(--border))] px-4 lg:px-5">
                <button
                  type="button"
                  className="inline-flex shrink-0 items-center text-sm font-medium text-[#0088cc] md:hidden"
                  onClick={() => {
                    setSelectedRole(null);
                    setIsEditMode(false);
                  }}
                >
                  ← Back
                </button>

                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <h2 className="truncate text-lg font-semibold text-[hsl(var(--foreground))]">
                      {selectedRole?.name}
                    </h2>
                    <span className="hidden shrink-0 rounded bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 sm:inline dark:text-emerald-400">
                      Active
                    </span>
                    <span className="hidden min-w-0 items-center gap-1 truncate text-xs text-[hsl(var(--muted-foreground))] sm:inline-flex">
                      <MdHistory className="h-3 w-3 shrink-0" />
                      {permissionCount} permissions
                    </span>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {!isEditMode ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setIsEditMode(true)}
                        className={erpPageBtnSecondaryClass}
                      >
                        <MdEdit className="h-4 w-4" />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteRole(selectedRole._id)}
                        className="inline-flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-5 text-sm font-semibold text-red-700 hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/60"
                      >
                        <MdDelete className="h-4 w-4" />
                        Delete
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={exitCreateOrEdit} className={erpPageBtnSecondaryClass}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={saveEdit}
                        disabled={loading}
                        className={erpPageBtnPrimaryClass}
                      >
                        {loading ? (
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        ) : (
                          <MdCheckCircle className="h-4 w-4" />
                        )}
                        Save
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="h-0 min-h-0 flex-1 overflow-y-auto px-4 py-4 pb-8 lg:px-5">
                <section className="mb-5 overflow-hidden rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                  <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 px-4 py-2.5">
                    <h3 className="text-sm font-semibold text-[hsl(var(--foreground))]">Role</h3>
                  </div>
                  <div className="px-4 py-3">
                    <p className="font-mono text-sm font-medium text-[hsl(var(--foreground))]">
                      {selectedRole?.key || "—"}
                    </p>
                  </div>
                </section>
                {renderPermissionCategories(isEditMode)}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
